import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, Pause, SkipForward, Loader2, Sparkles, Volume2, ChevronLeft, ChevronRight } from 'lucide-react';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', EVS: '🌿', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' };

export default function ExplainerVideoPlayer({ params, onNavigate }) {
  const { api, user } = useAuth();
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const subject = params?.subject || 'Math';
  const chapterNum = params?.chapter || 1;
  const className = user?.class_name || 'Class 8';

  const generateVideo = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai/generate-explainer-video', {
        subject,
        chapter_number: parseInt(chapterNum),
        class_name: className,
      });
      setVideoData(res.data);
      if (res.data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio_base64}`);
        audioRef.current = audio;
        audio.onended = () => { setPlaying(false); };
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const slides = videoData?.video_data?.slides || [];
  const totalSlides = slides.length;

  const togglePlay = () => {
    if (playing) {
      audioRef.current?.pause();
      clearInterval(timerRef.current);
      setPlaying(false);
      setAutoAdvance(false);
    } else {
      audioRef.current?.play();
      setPlaying(true);
      setAutoAdvance(true);
      // Auto advance slides every ~20 seconds
      if (totalSlides > 0) {
        const interval = (audioRef.current?.duration || 180) / totalSlides * 1000;
        timerRef.current = setInterval(() => {
          setCurrentSlide(prev => {
            if (prev >= totalSlides - 1) {
              clearInterval(timerRef.current);
              setPlaying(false);
              setAutoAdvance(false);
              return prev;
            }
            return prev + 1;
          });
        }, Math.max(interval, 5000));
      }
    }
  };

  const nextSlide = () => setCurrentSlide(prev => Math.min(totalSlides - 1, prev + 1));
  const prevSlide = () => setCurrentSlide(prev => Math.max(0, prev - 1));

  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      clearInterval(timerRef.current);
    };
  }, []);

  // Stop audio and navigate
  const handleNavigate = (page, params) => {
    if (audioRef.current) { 
      audioRef.current.pause(); 
      audioRef.current = null; 
    }
    clearInterval(timerRef.current);
    setPlaying(false);
    onNavigate(page, params);
  };

  const slide = slides[currentSlide];

  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button data-testid="back-from-explainer" onClick={() => handleNavigate('chapters', { subject })} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-white font-['Outfit']" data-testid="explainer-title">Explainer Video</h1>
          <p className="text-xs text-slate-500">{subjectIcons[subject]} {subject} Ch{chapterNum} - {className}</p>
        </div>
      </div>

      {!videoData ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/20 flex items-center justify-center mx-auto mb-6">
            <Play className="w-12 h-12 text-violet-400 ml-1" />
          </div>
          <h2 className="text-lg font-bold text-white font-['Outfit'] mb-2">Generate Explainer Video</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">
            AI creates animated slides with voice narration for this chapter
          </p>
          <button data-testid="generate-explainer-btn" onClick={generateVideo} disabled={loading}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-8 h-12 font-semibold text-sm transition-all glow-violet flex items-center justify-center gap-2 mx-auto disabled:opacity-50">
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Creating Video...</> : <><Sparkles className="w-5 h-5" /> Generate Video</>}
          </button>
          {loading && <p className="text-xs text-slate-500 mt-4 animate-pulse">Generating slides & audio... ~30 seconds</p>}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="explainer-player">
          {/* Video Title */}
          <div className="text-center mb-2">
            <h2 className="text-base font-bold text-white font-['Outfit']">{videoData.video_data?.title || videoData.chapter_name}</h2>
            <p className="text-xs text-slate-500">{videoData.duration_estimate} | {videoData.plays} plays</p>
          </div>

          {/* Slide Display */}
          <div className="relative overflow-hidden rounded-2xl" style={{ minHeight: '320px' }}>
            <AnimatePresence mode="wait">
              {slide && (
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  className="rounded-2xl p-6 border border-slate-700/50"
                  style={{ background: slide.background_color ? `${slide.background_color}15` : 'rgba(15,23,42,0.7)', minHeight: '320px' }}
                  data-testid={`slide-${currentSlide}`}
                >
                  {/* Slide Number */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">Slide {slide.slide_number}/{totalSlides}</span>
                    {playing && <Volume2 className="w-4 h-4 text-violet-400 animate-pulse" />}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white font-['Outfit'] mb-4">{slide.title}</h3>

                  {/* Key Points */}
                  {slide.key_points?.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {slide.key_points.map((point, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.2 }}
                          className="flex items-start gap-2">
                          <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: slide.background_color || '#7c3aed' }} />
                          <p className="text-sm text-slate-200">{point}</p>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Visual Description */}
                  {slide.visual_description && (
                    <div className="bg-slate-900/50 border border-slate-700/30 rounded-xl p-3 mt-3">
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Visual</p>
                      <p className="text-xs text-cyan-300 italic">{slide.visual_description}</p>
                    </div>
                  )}

                  {/* Narration */}
                  <div className="mt-4 bg-slate-800/30 rounded-xl p-3">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Narration</p>
                    <p className="text-xs text-slate-300 leading-relaxed">{slide.narration}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Slide Progress */}
          <div className="flex gap-1">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setCurrentSlide(i)}
                className={`h-1.5 flex-1 rounded-full transition-all ${i === currentSlide ? 'bg-violet-500' : i < currentSlide ? 'bg-violet-500/40' : 'bg-slate-800'}`}
                data-testid={`slide-dot-${i}`} />
            ))}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4">
            <button data-testid="prev-slide-btn" onClick={prevSlide} disabled={currentSlide === 0}
              className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 hover:bg-slate-700 disabled:opacity-30">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button data-testid="play-video-btn" onClick={togglePlay} disabled={!videoData.has_audio}
              className="w-16 h-16 rounded-full bg-violet-600 hover:bg-violet-700 flex items-center justify-center transition-all glow-violet disabled:opacity-50">
              {playing ? <Pause className="w-7 h-7 text-white" /> : <Play className="w-7 h-7 text-white ml-1" />}
            </button>
            <button data-testid="next-slide-btn" onClick={nextSlide} disabled={currentSlide >= totalSlides - 1}
              className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 hover:bg-slate-700 disabled:opacity-30">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {!videoData.has_audio && (
            <p className="text-xs text-amber-400 text-center bg-amber-500/10 rounded-lg py-2 px-3">
              Audio processing - slides and narration text available. Audio coming soon.
            </p>
          )}

          {/* Rewards */}
          <div className="text-center text-xs text-violet-400 bg-violet-500/5 border border-violet-500/10 rounded-xl py-3">
            +50 XP | +25 Coins | Video Master Badge!
          </div>

          <div className="flex gap-3">
            <button data-testid="take-quiz-after-video" onClick={() => handleNavigate('chapters', { subject })}
              className="flex-1 bg-amber-600/10 border border-amber-500/20 text-amber-300 rounded-xl py-3 text-sm font-medium hover:bg-amber-600/20">
              Take Quiz
            </button>
            <button data-testid="video-library-link" onClick={() => handleNavigate('library')}
              className="flex-1 bg-slate-800 text-slate-300 rounded-xl py-3 text-sm font-medium hover:bg-slate-700">
              Video Library
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
